<template>
  <div class="smart-customer-search mx-auto w-100" :class="[{ 'hidden-on-fullscreen': isFullscreenDialogOpen }]">
    <div class="toolbar-row">
      <!-- Bouton menu mobile -->
      <v-btn icon class="mobile-menu-btn" v-if="isCompact" title="Menu" aria-label="Ouvrir le menu" @click.stop="$emit('toggle-drawer')">
        <v-icon>menu</v-icon>
      </v-btn>
      <!-- Main search button -->
      <v-btn
        @click="openSpotlight"
        :class="[
          'spotlight-trigger-btn',
          { 'client-selected': selectedCustomer && selectedCustomer.name,
            'has-clear': showClearBtn }
        ]"
        outlined
        depressed
      >
        <v-icon left>search</v-icon>

        <span v-if="selectedCustomer && selectedCustomer.name" class="selected-text ios-customer-row">
          <v-avatar size="22" class="mr-1">
            <img :src="getClientLogo()" alt="logo" class="logo-img" />
          </v-avatar>
          <span v-if="selectedCustomer.idenseigne" class="enseigne-badge-ios">
            {{ getEnseigneName(selectedCustomer.idenseigne) }}
          </span>
          <span class="client-name-ios">{{ selectedCustomer.name }}</span>
          <span v-if="selectedCustomer.clientId" class="client-id-ios">({{ selectedCustomer.clientId }})</span>
        </span>

        <span v-else class="placeholder-text">Rechercher un client...</span>

        <!-- Actions à droite : Clear (optionnel) + ⌘K (toujours) -->
        <div class="spotlight-affordance">
          <span class="spotlight-kbd-apple" aria-hidden="true">⌘+K</span>
          <v-btn
            v-if="showClearBtn"
            icon
            class="clear-customer-btn ml-1"
            @click.stop="$emit('clear')"
            title="Effacer la sélection"
            aria-label="Effacer la sélection"
            tabindex="-1"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </div>
      </v-btn>

      <div v-if="selectedCustomer && selectedCustomer.clientId" class="customer-actions d-none d-md-flex">
      <!-- Copy serial -->
      <v-tooltip bottom v-if="selectedCustomer && selectedCustomer.serial">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            @click.stop="copyToClipboard(selectedCustomer.serial, 'Numéro de série')"
            icon small color="secondary" class="ml-2 copy-serial-btn"
          >
            <v-icon small>content_copy</v-icon>
          </v-btn>
        </template>
        <span>Copier le numéro de série</span>
        <pre>{{selectedCustomer.serial}}</pre>
      </v-tooltip>

      <!-- QR Code -->
      <v-tooltip bottom v-if="selectedCustomer && selectedCustomer.serial">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            @click.stop="showQrCode"
            icon small color="primary" class="ml-2 qr-code-btn"
          >
            <v-icon small>qr_code</v-icon>
          </v-btn>
        </template>
        <span>Afficher le QR Code</span>
      </v-tooltip>

      <!-- Download database -->
      <v-tooltip bottom v-if="latestDatabase && selectedCustomer && selectedCustomer.clientId">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            :loading="downloadLoading"
            @click.stop="downloadLatestDatabase"
            icon small color="primary" class="ml-2 download-db-btn"
          >
            <v-icon small>cloud_download</v-icon>
          </v-btn>
        </template>
        <span>Télécharger la sauvegarde {{ formatDatabaseTimeWithDate(latestDatabase.date) }}</span>
      </v-tooltip>

      <!-- Impersonate (admin Hublot uniquement) -->
      <v-tooltip bottom v-if="selectedCustomer && selectedCustomer.clientId && isHublotAdmin">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            :loading="impersonateLoading"
            :disabled="impersonateLoading"
            @click.stop="impersonateSelectedCustomer"
            icon small color="secondary" class="ml-2 impersonate-btn"
          >
            <v-icon small>support_agent</v-icon>
          </v-btn>
        </template>
        <span>Impersonate ce magasin</span>
      </v-tooltip>
      </div>
      <v-menu v-if="selectedCustomer && selectedCustomer.clientId" offset-y left nudge-bottom="6">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon small class="d-md-none" v-bind="attrs" v-on="on" title="Actions sur le magasin" aria-label="Actions sur le magasin">
            <v-icon small>more_vert</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item v-if="selectedCustomer.serial" @click="copyToClipboard(selectedCustomer.serial, 'Numéro de série')">
            <v-list-item-icon><v-icon small>content_copy</v-icon></v-list-item-icon>
            <v-list-item-title>Copier le numéro de série</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="selectedCustomer.serial" @click="showQrCode">
            <v-list-item-icon><v-icon small>qr_code</v-icon></v-list-item-icon>
            <v-list-item-title>Afficher le QR code</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="latestDatabase" :disabled="downloadLoading" @click="downloadLatestDatabase">
            <v-list-item-icon><v-icon small>cloud_download</v-icon></v-list-item-icon>
            <v-list-item-title>Télécharger la sauvegarde</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isHublotAdmin" :disabled="impersonateLoading" @click="impersonateSelectedCustomer">
            <v-list-item-icon><v-icon small>support_agent</v-icon></v-list-item-icon>
            <v-list-item-title>Impersonate ce magasin</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- Modale QR Code -->
    <QrCodeModal 
      :show.sync="showQrModal" 
      :customer="selectedCustomer"
    />
  </div>
</template>

<script>
  import moment from "moment";
  import { mapState, mapGetters } from "vuex";
  import CustomerService from "../services/CustomerService";
  import DatabaseService from "../services/DatabaseService";
  import QrCodeModal from "./QrCodeModal.vue";
  import MobileMixin from "@/mixins/MobileMixin";

  export default {
    data() {
      return {
        showQrModal: false,
        downloadLoading: false
      };
    },
    name: "SelectCustomer",
    components: {
      QrCodeModal
    },
    mixins: [MobileMixin],
    props: {
      selectedCustomer: { type: Object, default: () => ({}) },
      isFullscreenDialogOpen: { type: Boolean, default: false },
      latestDatabase: { type: Object, default: () => null },
      showClearBtn: { type: Boolean, default: false },
    },

    emits: ["clear", "open-spotlight", "copied"],
    computed: {
      // Sous 960 px : hamburger dans la barre et actions du magasin dans un menu
      isCompact () {
        return this.$vuetify.breakpoint.mobile
      },
      ...mapState({
        // selectedCustomer: (state) => state.customers.selected,
        databases: (state) => state.home.databases,
        allDatabases: (state) => state.home.allDatabases,
        notionClients: (state) => state.customers.notionClients,
      }),
      ...mapGetters([
        "isHublotAdmin",
        "isImpersonating",
        "impersonateLoading",
      ]),
      notionClient() {
        if (!this.selectedCustomer?.serial || !this.notionClients) {
          return null;
        }
        return (
          this.notionClients.find(
            (client) => client.serial === this.selectedCustomer.serial,
          ) || null
        );
      },
    },
    methods: {
      openSpotlight() {
        this.$emit("open-spotlight");
      },
      logoEnseigne(idEnseigne) {
        return CustomerService.getLogo(idEnseigne);
      },

      getClientLogo() {
        // Prioriser le logo Notion s'il est disponible
        if (
          this.notionClient &&
          this.notionClient.notion &&
          this.notionClient.notion.icone
        ) {
          return this.notionClient.notion.icone;
        }
        // Sinon utiliser le logo de l'enseigne
        if (this.selectedCustomer.idenseigne) {
          return this.logoEnseigne(this.selectedCustomer.idenseigne);
        }
        // Fallback vers une icône par défaut
        return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJMMTMuMDkgOC4yNkwyMCA9TDEzLjA5IDE1Ljc0TDEyIDIyTDEwLjkxIDE1Ljc0TDQgOUwxMC45MSA4LjI2TDEyIDJaIiBmaWxsPSIjMzc2M0I0Ii8+Cjwvc3ZnPgo=";
      },
      getEnseigneName(idEnseigne) {
        // Retourne le nom de l'enseigne basé sur l'ID
        const enseigneMap = {
          5: "Carrefour",
          1: "E.Leclerc",
          3: "Intermarché",
          2: "Super U",
          4: "Indépendant",
        };
        return enseigneMap[idEnseigne] || "Enseigne inconnue";
      },
      copyToClipboard(value, label = "Valeur") {
        if (!value) return;
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(value).then(() => {
          this.$toast?.success?.(`${label} copiée !`) || this.$emit("copied", { label, value });
          });
        } else {
          const area = document.createElement("textarea");
          area.value = value;
          document.body.appendChild(area);
          area.select();
          document.execCommand("copy");
          document.body.removeChild(area);
        this.$toast?.success?.(`${label} copiée !`) || this.$emit("copied", { label, value });
        }
      },
      showQrCode() {
        this.showQrModal = true;
      },
      impersonateSelectedCustomer() {
        if (!this.selectedCustomer?.clientId) return;
        this.$store
          .dispatch("impersonateCustomer", this.selectedCustomer)
          .catch((error) => {
            console.error("Erreur lors de l'impersonation:", error);
            this.$store.dispatch("addNotification", {
              text: "Impossible d'impersonate ce magasin",
              color: "error",
            });
          });
      },
      downloadLatestDatabase() {
        if (!this.latestDatabase) {
          this.$store.dispatch("addNotification", {
            text: "Aucune base de données disponible pour ce client",
            color: "warning",
          });
          return;
        }
        
        this.downloadLoading = true;
        const formattedDate = moment(this.latestDatabase.date).format(
          "DD-MM-YYYY_HH-mm",
        );
        
        DatabaseService.download(
          this.latestDatabase.uuid,
          this.selectedCustomer.name,
          formattedDate,
          this.latestDatabase.aux,
        )
          .then(() => {
            this.$store.dispatch("addNotification", {
              text: "Téléchargement de la base de données démarré",
              color: "success",
            });
          })
          .catch((error) => {
            console.error("Erreur lors du téléchargement:", error);
            this.$store.dispatch("addNotification", {
              text: "Erreur lors du téléchargement de la base de données",
              color: "error",
            });
          })
          .finally(() => {
            this.downloadLoading = false;
          });
      },
      formatDatabaseTimeWithDate(d) {
        if (!d) return "";
        const date = typeof d === "string" ? new Date(d) : d;
        return date && typeof date.toLocaleString === 'function' ? date.toLocaleString() : String(d);
      },
    },
  };
</script>

<style lang="scss" scoped>
/* ——— Mise en page ——— */
.smart-customer-search {
  width: 100%;
}

.toolbar-row {
  display: flex;
  align-items: center;
  width: 100%;
  gap: var(--space-2);
  min-width: 0;
  position: relative;
}

/* ——— Déclencheur de la recherche : un champ de 34 px, clair, sans ombre ——— */
.customer-actions {
  align-items: center;
  flex: none;
}
.spotlight-trigger-btn {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  padding: 0 var(--space-2) 0 var(--space-3) !important;
  background: var(--surface-raised) !important;
  border: 1px solid var(--line-strong) !important;
  border-radius: var(--radius-md) !important;
  color: var(--ink) !important;
  text-transform: none !important;
  font-weight: 400;
  letter-spacing: 0;
  box-shadow: none !important;
  justify-content: flex-start !important;

  .v-btn__content {
    width: 100%;
    min-width: 0;
    justify-content: flex-start;
    align-items: center;
    gap: var(--space-2);
    overflow: hidden;
  }

  .v-icon { color: var(--ink-muted) !important; }
  .placeholder-text { color: var(--ink-muted); }

  &:hover { border-color: var(--ink-muted) !important; }

  &.client-selected {
    border-color: var(--brand) !important;
    .v-icon { color: var(--brand) !important; }
  }
}

/* Actions à droite dans le bouton : raccourci clavier et effacement */
.spotlight-affordance {
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.spotlight-kbd-apple {
  font: var(--text-small);
  font-family: var(--font-mono);
  color: var(--ink-muted);
  background: var(--surface-sunken);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 0 6px;
  line-height: 20px;
  user-select: none;
  pointer-events: none;
}

.clear-customer-btn {
  width: 24px !important;
  height: 24px !important;

  .v-icon { font-size: 16px !important; color: var(--ink-muted) !important; }
  &:hover .v-icon { color: var(--danger) !important; }
}

/* ——— Client sélectionné : logo, enseigne, nom, identifiant ——— */
.logo-img {
  object-fit: cover;
  background: var(--surface-raised);
  border-radius: 50%;
  border: 1px solid var(--line);
}

.selected-text {
  font-weight: 500;
}

.ios-customer-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  white-space: nowrap;
}

.enseigne-badge-ios {
  font: var(--text-label);
  color: var(--ink-muted);
  background: var(--surface-sunken);
  border: 1px solid var(--line);
  border-radius: var(--radius-full);
  padding: 0 var(--space-2);
  line-height: 20px;
  flex-shrink: 0;
}

.client-name-ios {
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1 1 auto;
}

.client-id-ios {
  color: var(--ink-muted);
  flex-shrink: 0;
}

@media (max-width: 1280px) {
  .enseigne-badge-ios {
    display: none;
  }
}

/* Téléphone : le raccourci clavier et les actions secondaires disparaissent, le menu apparaît */
@media (max-width: 599.98px) {
  .toolbar-row {
    gap: var(--space-1);
  }

  .spotlight-kbd-apple {
    display: none;
  }

  .impersonate-btn,
  .download-db-btn {
    display: none;
  }

  .mobile-menu-btn {
    background: var(--surface-raised);
    border: 1px solid var(--line);
    .v-icon { color: var(--brand); }
  }
}

/* Petit écran : le bouton se compresse, le nom du magasin se tronque, le superflu disparaît */
.spotlight-trigger-btn ::v-deep .v-btn__content { min-width: 0; max-width: 100%; justify-content: flex-start; }
.selected-text { min-width: 0; overflow: hidden; }
.client-name-ios { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
@media (max-width: 959.98px) {
  .spotlight-kbd-apple { display: none; }
}
@media (max-width: 599.98px) {
  .enseigne-badge-ios, .client-id-ios, .placeholder-text { display: none; }
  .spotlight-trigger-btn { padding: 0 var(--space-1) 0 var(--space-2) !important; }
  .toolbar-row { gap: var(--space-1); }
}
</style>
